import React, { useEffect, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { http } from '../../api/http';
import { toast } from 'sonner';
import { GiftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface MilestoneTier {
  tier: number;
  pointsRequired: number;
  tokensEarned: string;
}

interface ReachedMilestone {
  id: number;
  seasonId: number;
  wallet: string;
  milestoneTier: number;
  pointsRequired: number;
  tokensEarned: string;
  reachedAt: string;
}

interface ClaimSummary {
  unclaimedMilestoneTokens: string;
  unclaimedReferralTokens: string;
  totalClaimable: string;
  totalClaimed: string;
}

export const TokenRewardsPanel: React.FC<{ currentPoints?: number }> = ({ currentPoints = 0 }) => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();
  const [summary, setSummary] = useState<ClaimSummary | null>(null);
  const [milestones, setMilestones] = useState<ReachedMilestone[]>([]);
  const [tiers, setTiers] = useState<MilestoneTier[]>([]);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!wallet) return;
    Promise.all([
      http.get<ClaimSummary>(`/rewards/${wallet}/summary`),
      http.get<{ milestones: ReachedMilestone[]; tiers: MilestoneTier[] }>(
        `/rewards/${wallet}/milestones`,
      ),
    ])
      .then(([s, m]) => {
        setSummary(s);
        setMilestones(m.milestones);
        setTiers(m.tiers);
      })
      .catch(console.error);
  }, [wallet]);

  const handleClaim = async () => {
    if (!wallet) return;
    setClaiming(true);
    try {
      const result = await http.post<{ tokensClaimed: string; txSignature: string }>(
        '/rewards/claim',
        { wallet },
      );
      toast.success(`Claimed ${Number(result.tokensClaimed).toLocaleString()} tokens!`);
      // Refresh summary
      const s = await http.get<ClaimSummary>(`/rewards/${wallet}/summary`);
      setSummary(s);
    } catch {
      toast.error('Nothing to claim');
    } finally {
      setClaiming(false);
    }
  };

  if (!wallet) return null;

  const reachedTiers = new Set(milestones.map((m) => m.milestoneTier));
  const totalClaimable = Number(summary?.totalClaimable ?? 0);

  // Find next milestone
  const nextTier = tiers.find((t) => !reachedTiers.has(t.tier) && currentPoints < t.pointsRequired);
  const progressToNext = nextTier
    ? Math.min(100, (currentPoints / nextTier.pointsRequired) * 100)
    : 100;

  return (
    <div className="arcade-panel pixel-corners rounded-xl p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg text-white/80">Token Rewards</h2>
        {totalClaimable > 0 && (
          <button
            onClick={handleClaim}
            disabled={claiming}
            className="btn-press rounded-lg border border-arcade-green/50 bg-arcade-green/20 px-4 py-2 font-display text-xs uppercase text-arcade-green transition hover:bg-arcade-green/30 disabled:opacity-50"
          >
            {claiming ? 'Claiming...' : `Claim ${totalClaimable.toLocaleString()} Tokens`}
          </button>
        )}
      </div>

      {/* Claimable Breakdown */}
      {summary && (
        <div className="mb-4 grid grid-cols-2 gap-3 font-pixel text-xs">
          <div className="rounded-lg bg-white/5 p-3">
            <div className="text-white/40">From Milestones</div>
            <div className="text-arcade-cyan">
              {Number(summary.unclaimedMilestoneTokens).toLocaleString()}
            </div>
          </div>
          <div className="rounded-lg bg-white/5 p-3">
            <div className="text-white/40">From Referrals</div>
            <div className="text-arcade-pink">
              {Number(summary.unclaimedReferralTokens).toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Next Milestone Progress */}
      {nextTier && (
        <div className="mb-4">
          <div className="flex justify-between font-pixel text-[10px] text-white/40">
            <span>{currentPoints.toLocaleString()} pts</span>
            <span>
              Tier {nextTier.tier}: {nextTier.pointsRequired.toLocaleString()} pts (+
              {Number(nextTier.tokensEarned).toLocaleString()} tokens)
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-arcade-cyan to-arcade-green transition-all duration-500"
              style={{ width: `${progressToNext}%` }}
            />
          </div>
        </div>
      )}

      {/* Milestone Tiers */}
      <div className="space-y-1.5">
        {tiers.map((t) => {
          const reached = reachedTiers.has(t.tier);
          return (
            <div
              key={t.tier}
              className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                reached ? 'bg-arcade-green/10' : 'bg-white/5'
              }`}
            >
              <div className="flex items-center gap-2">
                {reached ? (
                  <CheckCircleIcon className="h-4 w-4 text-arcade-green" />
                ) : (
                  <GiftIcon className="h-4 w-4 text-white/30" />
                )}
                <span className="font-pixel text-xs text-white/60">
                  {t.pointsRequired.toLocaleString()} pts
                </span>
              </div>
              <span
                className={`font-pixel text-xs ${reached ? 'text-arcade-green' : 'text-white/40'}`}
              >
                +{Number(t.tokensEarned).toLocaleString()} tokens
              </span>
            </div>
          );
        })}
      </div>

      {/* Total Claimed */}
      {summary && Number(summary.totalClaimed) > 0 && (
        <div className="mt-3 text-center font-pixel text-[10px] text-white/30">
          Total claimed: {Number(summary.totalClaimed).toLocaleString()} tokens
        </div>
      )}
    </div>
  );
};
